import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Save as SaveIcon,
  Upload as UploadIcon,
  Description as DescriptionIcon,
  TrendingDown as TrendingDownIcon,
  Balance as BalanceIcon,
  TrendingUp as TrendingUpIcon,
  Edit as EditIcon,
} from "@mui/icons-material";

import { plentyArticlesApi } from "../../../../services/api/plenty/articles";
import { pennerRennerApi } from "../../../../services/api/plenty/penner_renner";
import { sheetsSettingsApi } from "../../../../services/api/plenty/sheets_settings";
import { googleApi } from "../../../../services/api/plenty/google_credentials";
const getErrorMessage = (error) => {
  if (error.response && error.response.data && error.response.data.detail) {
    return error.response.data.detail;
  }
  return error.message || "An unexpected error occurred";
};

const PennerRennerManagementSubTab = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [autoRefreshInfo, setAutoRefreshInfo] = useState(null);

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Plenty Penner Renner
  const [plentyRules, setPlentyRules] = useState([]);
  const [plentyEditValues, setPlentyEditValues] = useState({});
  const [loadingPlentyRules, setLoadingPlentyRules] = useState(false);
  const [savingPlenty, setSavingPlenty] = useState({});
  const [plentyRuleError, setPlentyRuleError] = useState("");

  // Amazon Penner Renner
  const [amazonRules, setAmazonRules] = useState([]);
  const [amazonEditValues, setAmazonEditValues] = useState({});
  const [loadingAmazonRules, setLoadingAmazonRules] = useState(false);
  const [savingAmazon, setSavingAmazon] = useState({});
  const [amazonRuleError, setAmazonRuleError] = useState("");

  // CSV Upload state
  const [csvFile, setCsvFile] = useState(null);
  const [csvUploadLoading, setCsvUploadLoading] = useState(false);
  const [csvUploadResults, setCsvUploadResults] = useState(null);

  // Sheet update state
  const [updateStep, setUpdateStep] = useState("sheets"); // 'sheets', 'plenty', 'amazon', 'done'
  const [updatingSheets, setUpdatingSheets] = useState(false);

  // System status state
  const [systemStatus, setSystemStatus] = useState({
    isReady: false,
    hasActiveGoogleCredential: false,
    hasValidSheetSettings: false,
  });

  // Check system status on mount
  useEffect(() => {
    checkSystemStatus();
  }, []);

  // Load Plenty Rules
  useEffect(() => {
    setLoadingPlentyRules(true);
    pennerRennerApi
      .getRules("plenty")
      .then((res) => {
        setPlentyRules(res.data);
        setPlentyEditValues(
          Object.fromEntries(
            res.data.map((r) => [r.rule_name, r.threshold_value]),
          ),
        );
        setLoadingPlentyRules(false);
      })
      .catch((err) => {
        setPlentyRuleError("Failed to load Plenty rules");
        setLoadingPlentyRules(false);
      });
  }, []);

  // Load Amazon Rules
  useEffect(() => {
    setLoadingAmazonRules(true);
    pennerRennerApi
      .getRules("amazon")
      .then((res) => {
        setAmazonRules(res.data);
        setAmazonEditValues(
          Object.fromEntries(
            res.data.map((r) => [r.rule_name, r.threshold_value]),
          ),
        );
        setLoadingAmazonRules(false);
      })
      .catch((err) => {
        setAmazonRuleError("Failed to load Amazon rules");
        setLoadingAmazonRules(false);
      });
  }, []);

  const handleSaveRule = (ruleName, type) => {
    const isPlenty = type === "plenty";
    const setSaving = isPlenty ? setSavingPlenty : setSavingAmazon;
    const setError = isPlenty ? setPlentyRuleError : setAmazonRuleError;
    const editValues = isPlenty ? plentyEditValues : amazonEditValues;

    setSaving((prev) => ({ ...prev, [ruleName]: true }));
    pennerRennerApi
      .updateRule(ruleName, parseFloat(editValues[ruleName]), type)
      .then((res) => {
        const setRules = isPlenty ? setPlentyRules : setAmazonRules;
        setRules((prev) =>
          prev.map((r) =>
            r.rule_name === ruleName
              ? { ...r, threshold_value: res.data.threshold_value }
              : r,
          ),
        );
        setSaving((prev) => ({ ...prev, [ruleName]: false }));
      })
      .catch(() => {
        setError("Failed to save rule");
        setSaving((prev) => ({ ...prev, [ruleName]: false }));
      });
  };

  const handleEditChange = (ruleName, value, type) => {
    const setEditValues =
      type === "plenty" ? setPlentyEditValues : setAmazonEditValues;
    setEditValues((prev) => ({
      ...prev,
      [ruleName]: value,
    }));
  };

  // CSV Upload function
  const handleCsvUpload = async () => {
    if (!csvFile) {
      setError("Please select a CSV file first");
      return;
    }

    try {
      setCsvUploadLoading(true);
      setError(null);
      setCsvUploadResults(null);

      const response = await plentyArticlesApi.update_items_by_csv(csvFile);
      setCsvUploadResults(response.data);
      setSuccess(
        `CSV upload completed! Processed ${response.data.matched_items} items (${response.data.articles_created} created, ${response.data.articles_updated} updated)`,
      );

      // Clear the file input
      setCsvFile(null);
    } catch (error) {
      console.error("Error uploading CSV:", error);
      setError(getErrorMessage(error));
    } finally {
      setCsvUploadLoading(false);
    }
  };

  const checkSystemStatus = async () => {
    try {
      // Check for active Google credential
      const googleResponse = await googleApi.getCredentials();
      const googleCredentials = googleResponse.data || [];
      const hasActiveGoogleCredential = googleCredentials.some(
        (cred) => cred.active_plenty === true && cred.status === "authorized",
      );

      // Check for valid CSV sheet settings
      const csvResponse = await sheetsSettingsApi.getSettings("csv");
      const csvSettings = csvResponse.data;
      const hasValidSheetSettings = !!csvSettings.spreadsheet_id;

      const isReady = hasActiveGoogleCredential && hasValidSheetSettings;

      setSystemStatus({
        isReady,
        hasActiveGoogleCredential,
        hasValidSheetSettings,
      });

      return {
        isReady,
        hasActiveGoogleCredential,
        hasValidSheetSettings,
      };
    } catch (error) {
      console.error("Failed to check system status:", error);
      setSystemStatus({
        isReady: false,
        hasActiveGoogleCredential: false,
        hasValidSheetSettings: false,
      });
      return {
        isReady: false,
        hasActiveGoogleCredential: false,
        hasValidSheetSettings: false,
      };
    }
  };

  const handleUpdateSheets = async () => {
    setUpdatingSheets(true);
    setError(null);
    setSuccess(null);
    try {
      // Update Plenty sheets first
      setUpdateStep("updating-plenty");
      await plentyArticlesApi.updateSheets("plenty");
      setSuccess(
        "Plenty sheets updated successfully. Updating Amazon sheets...",
      );

      // Update Amazon sheets second
      setUpdateStep("updating-amazon");
      await plentyArticlesApi.updateSheets("amazon");
      setSuccess("All sheets updated successfully.");

      // Reset to initial state
      setUpdateStep("sheets");
    } catch (err) {
      setError(`Failed to update sheets: ${err.message || err}`);
      setUpdateStep("sheets"); // Reset on error
    } finally {
      setUpdatingSheets(false);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Utils Card for Update Sheets and Recategorize Articles */}
          <Card
            sx={{
              border: "2px solid",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Utilities
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Use these utilities to synchronize Google Sheets and
                  recategorize articles in the database.
                </Typography>
              </Alert>

              {/* Color legend */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2">Color legend</Typography>
                <Typography variant="body2" component="div">
                  <strong style={{ color: "rgb(34,139,34)" }}>Green:</strong>{" "}
                  Renner articles — high-selling items that qualify as "Renner".
                  <br />
                  <strong style={{ color: "rgb(220,20,60)" }}>Red:</strong>{" "}
                  Penner articles — low-selling items that qualify as "Penner".
                  <br />
                  <strong style={{ color: "rgb(30,144,255)" }}>
                    Blue:
                  </strong>{" "}
                  Neutral articles — items that don't qualify as either Penner
                  or Renner.
                  <br />
                  <strong style={{ color: "gray" }}>Gray:</strong> Out of stock
                  — the article is currently out of stock (OOS).
                  <br />
                  <em style={{ display: "block", marginTop: 6 }}>
                    Notes: Articles automatically switch colors when they change
                    categories (e.g., from Penner to Renner). The system
                    highlights both the SKU column and a marker column (column
                    F) to make category changes easy to spot.
                  </em>
                </Typography>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleUpdateSheets}
                  disabled={
                    updatingSheets ||
                    !systemStatus.hasActiveGoogleCredential ||
                    !systemStatus.hasValidSheetSettings
                  }
                  fullWidth
                  startIcon={
                    updatingSheets ? (
                      <CircularProgress size={20} />
                    ) : (
                      <DescriptionIcon />
                    )
                  }
                >
                  {updatingSheets
                    ? updateStep === "updating-plenty"
                      ? "Updating Plenty Sheets..."
                      : updateStep === "updating-amazon"
                        ? "Updating Amazon Sheets..."
                        : "Updating..."
                    : "Update Sheets"}
                </Button>
                <Typography variant="caption" color="textSecondary">
                  Update Sheets: synchronizes the Penner/Renner/OOS Google
                  Sheets with the current database categorization and
                  stock/sales info. Automatically updates Plenty sheets first,
                  then Amazon sheets. Highlights rows and appends missing
                  articles.
                </Typography>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={async () => {
                    setLoading(true);
                    setSuccess(null);
                    setError(null);
                    try {
                      const res = await plentyArticlesApi.categorizeArticles();
                      setSuccess("Articles recategorized successfully.");
                    } catch (err) {
                      setError("Failed to recategorize articles.");
                    } finally {
                      setLoading(false);
                    }
                  }}
                  disabled={loading}
                  fullWidth
                  startIcon={
                    loading ? <CircularProgress size={20} /> : <UploadIcon />
                  }
                >
                  {loading ? "Recategorizing..." : "Recategorize Articles"}
                </Button>
                <Typography variant="caption" color="textSecondary">
                  Recategorize Articles: recalculates penner/renner/neutral
                  types for all articles using current thresholds and updates
                  the database (no sheet changes).
                </Typography>
              </Box>
            </CardContent>
          </Card>
          {/* CSV Upload Card */}
          <Card
            sx={{
              border: "2px solid",
              borderColor: "primary.main",
              borderRadius: 2,
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom>
                CSV Upload
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Upload a CSV file with SKUs (first column) to update multiple
                  articles at once. The system will search, enrich, categorize,
                  and update articles automatically.
                </Typography>
                <Typography variant="body2">
                  The System will ignore the first value, as it is preserved for
                  a heading by default.
                </Typography>
              </Alert>

              {!systemStatus.hasValidSheetSettings && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Please configure CSV sheet settings first (including
                    spreadsheet URL) in the Sheet Settings tab before uploading
                    CSV files.
                  </Typography>
                </Alert>
              )}

              {!systemStatus.hasActiveGoogleCredential && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Please configure and activate a Google credential in the
                    Credentials tab before uploading CSV files.
                  </Typography>
                </Alert>
              )}

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Button
                  variant="outlined"
                  component={systemStatus.isReady ? "label" : "button"}
                  startIcon={<UploadIcon />}
                  fullWidth
                  disabled={!systemStatus.isReady}
                  sx={{
                    cursor: systemStatus.isReady ? "pointer" : "not-allowed",
                  }}
                >
                  Select CSV File
                  {systemStatus.isReady && (
                    <input
                      type="file"
                      hidden
                      accept=".csv"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setCsvFile(file);
                          setError(null);
                        }
                      }}
                    />
                  )}
                </Button>

                {csvFile && (
                  <Alert severity="success" sx={{ mb: 1 }}>
                    <Typography variant="body2">
                      Selected: {csvFile.name} (
                      {(csvFile.size / 1024).toFixed(2)} KB)
                    </Typography>
                  </Alert>
                )}

                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCsvUpload}
                  disabled={
                    !systemStatus.isReady || !csvFile || csvUploadLoading
                  }
                  fullWidth
                  startIcon={
                    csvUploadLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <UploadIcon />
                    )
                  }
                >
                  {csvUploadLoading ? "Uploading..." : "Upload & Process CSV"}
                </Button>

                {csvUploadResults && (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      <strong>CSV Upload Results:</strong>
                      <br />
                      Total SKUs: {csvUploadResults.total_skus}
                      <br />
                      Matched Items: {csvUploadResults.matched_items}
                      <br />
                      Created: {csvUploadResults.articles_created}
                      <br />
                      Updated: {csvUploadResults.articles_updated}
                      <br />
                      Failed: {csvUploadResults.failed_articles}
                      <br />
                      Time:{" "}
                      {csvUploadResults.processing_time_seconds?.toFixed(2)}s
                    </Typography>
                  </Alert>
                )}
              </Box>
            </CardContent>
          </Card>

          <Card
            sx={{
              border: "3px solid",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
              >
                <Typography variant="h6">Penner/Renner Rules</Typography>
              </Box>
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                sx={{ mb: 3 }}
              >
                <Tab label="Plenty Articles" />
                <Tab label="Amazon Articles" />
              </Tabs>
              {activeTab === 0 && (
                <Box>
                  <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                      Set the thresholds for categorizing{" "}
                      <strong>Plenty</strong> articles:
                      <br />
                      <strong>Penner:</strong> ratio ≤ penner threshold
                      <br />
                      <strong>Renner:</strong> ratio ≥ renner threshold
                      <br />
                      <strong>Neutral:</strong> between thresholds
                      <br />
                      Ratio = sold_articles / netto_stock
                    </Typography>
                  </Alert>
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                      <strong>Important:</strong> After changing threshold
                      values, you must click the "Recategorize Articles" button
                      in the Utilities section above to apply the new thresholds
                      to existing articles in the database.
                    </Typography>
                  </Alert>
                  {loadingPlentyRules ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", p: 2 }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : (
                    (() => {
                      const pennerRule = plentyRules.find(
                        (r) => r.rule_name === "penner",
                      );
                      const rennerRule = plentyRules.find(
                        (r) => r.rule_name === "renner",
                      );
                      return (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 4,
                            width: "100%",
                          }}
                        >
                          {/* Penner */}
                          <Card
                            sx={{
                              border: "3px solid #ff9800",
                              borderRadius: 3,
                              background:
                                "linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)",
                              boxShadow: "0 4px 12px rgba(255, 152, 0, 0.15)",
                            }}
                          >
                            <CardContent sx={{ p: 4 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 2,
                                  mb: 3,
                                }}
                              >
                                <TrendingDownIcon
                                  sx={{
                                    fontSize: 40,
                                    color: "#e65100",
                                  }}
                                />
                                <Box>
                                  <Typography
                                    variant="h5"
                                    sx={{
                                      color: "#e65100",
                                      fontWeight: "bold",
                                      mb: 1,
                                    }}
                                  >
                                    Penner Articles
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: "#bf360c" }}
                                  >
                                    Low-selling items • Ratio ≥{" "}
                                    {pennerRule
                                      ? pennerRule.threshold_value
                                      : "-"}
                                  </Typography>
                                </Box>
                              </Box>

                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 3,
                                  flexWrap: "wrap",
                                }}
                              >
                                <Box sx={{ flex: 1, minWidth: 200 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "#e65100",
                                      fontWeight: 500,
                                      mb: 1,
                                    }}
                                  >
                                    Threshold Value
                                  </Typography>
                                  <TextField
                                    type="number"
                                    size="small"
                                    value={
                                      plentyEditValues.penner ??
                                      (pennerRule
                                        ? pennerRule.threshold_value
                                        : "")
                                    }
                                    onChange={(e) =>
                                      handleEditChange(
                                        "penner",
                                        e.target.value,
                                        "plenty",
                                      )
                                    }
                                    inputProps={{
                                      step: "0.01",
                                      style: { fontSize: 18, fontWeight: 500 },
                                    }}
                                    sx={{
                                      "& .MuiInputBase-input": {
                                        fontSize: 18,
                                        fontWeight: 500,
                                      },
                                    }}
                                    fullWidth
                                  />
                                </Box>
                                <Button
                                  variant="contained"
                                  color="warning"
                                  size="large"
                                  startIcon={
                                    savingPlenty.penner ? (
                                      <CircularProgress size={20} />
                                    ) : (
                                      <SaveIcon />
                                    )
                                  }
                                  onClick={() =>
                                    handleSaveRule("penner", "plenty")
                                  }
                                  disabled={
                                    savingPlenty.penner ||
                                    plentyEditValues.penner ===
                                      (pennerRule
                                        ? pennerRule.threshold_value
                                        : undefined)
                                  }
                                  sx={{
                                    minWidth: 120,
                                    height: 48,
                                    fontSize: 16,
                                    fontWeight: "bold",
                                  }}
                                >
                                  {savingPlenty.penner ? "Saving..." : "Save"}
                                </Button>
                              </Box>
                            </CardContent>
                          </Card>

                          {/* Neutral */}
                          <Card
                            sx={{
                              border: "3px solid #43a047",
                              borderRadius: 3,
                              background:
                                "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)",
                              boxShadow: "0 4px 12px rgba(67, 160, 71, 0.15)",
                            }}
                          >
                            <CardContent sx={{ p: 4 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 2,
                                  mb: 2,
                                }}
                              >
                                <BalanceIcon
                                  sx={{
                                    fontSize: 40,
                                    color: "#2e7d32",
                                  }}
                                />
                                <Box>
                                  <Typography
                                    variant="h5"
                                    sx={{
                                      color: "#2e7d32",
                                      fontWeight: "bold",
                                      mb: 1,
                                    }}
                                  >
                                    Neutral Articles
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: "#1b5e20" }}
                                  >
                                    Balanced performance • Between thresholds
                                  </Typography>
                                </Box>
                              </Box>

                              <Box sx={{ mt: 2 }}>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    color: "#2e7d32",
                                    fontWeight: 500,
                                    textAlign: "center",
                                  }}
                                >
                                  {pennerRule && rennerRule
                                    ? `Ratio < ${pennerRule.threshold_value} and > ${rennerRule.threshold_value}`
                                    : "Articles with ratios between Penner and Renner thresholds"}
                                </Typography>
                              </Box>
                            </CardContent>
                          </Card>

                          {/* Renner */}
                          <Card
                            sx={{
                              border: "3px solid #1976d2",
                              borderRadius: 3,
                              background:
                                "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
                              boxShadow: "0 4px 12px rgba(25, 118, 210, 0.15)",
                            }}
                          >
                            <CardContent sx={{ p: 4 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 2,
                                  mb: 3,
                                }}
                              >
                                <TrendingUpIcon
                                  sx={{
                                    fontSize: 40,
                                    color: "#0d47a1",
                                  }}
                                />
                                <Box>
                                  <Typography
                                    variant="h5"
                                    sx={{
                                      color: "#0d47a1",
                                      fontWeight: "bold",
                                      mb: 1,
                                    }}
                                  >
                                    Renner Articles
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: "#0d47a1" }}
                                  >
                                    High-selling items • Ratio ≤{" "}
                                    {rennerRule
                                      ? rennerRule.threshold_value
                                      : "-"}
                                  </Typography>
                                </Box>
                              </Box>

                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 3,
                                  flexWrap: "wrap",
                                }}
                              >
                                <Box sx={{ flex: 1, minWidth: 200 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "#0d47a1",
                                      fontWeight: 500,
                                      mb: 1,
                                    }}
                                  >
                                    Threshold Value
                                  </Typography>
                                  <TextField
                                    type="number"
                                    size="small"
                                    value={
                                      plentyEditValues.renner ??
                                      (rennerRule
                                        ? rennerRule.threshold_value
                                        : "")
                                    }
                                    onChange={(e) =>
                                      handleEditChange(
                                        "renner",
                                        e.target.value,
                                        "plenty",
                                      )
                                    }
                                    inputProps={{
                                      step: "0.01",
                                      style: { fontSize: 18, fontWeight: 500 },
                                    }}
                                    sx={{
                                      "& .MuiInputBase-input": {
                                        fontSize: 18,
                                        fontWeight: 500,
                                      },
                                    }}
                                    fullWidth
                                  />
                                </Box>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  size="large"
                                  startIcon={
                                    savingPlenty.renner ? (
                                      <CircularProgress size={20} />
                                    ) : (
                                      <SaveIcon />
                                    )
                                  }
                                  onClick={() =>
                                    handleSaveRule("renner", "plenty")
                                  }
                                  disabled={
                                    savingPlenty.renner ||
                                    plentyEditValues.renner ===
                                      (rennerRule
                                        ? rennerRule.threshold_value
                                        : undefined)
                                  }
                                  sx={{
                                    minWidth: 120,
                                    height: 48,
                                    fontSize: 16,
                                    fontWeight: "bold",
                                  }}
                                >
                                  {savingPlenty.renner ? "Saving..." : "Save"}
                                </Button>
                              </Box>
                            </CardContent>
                          </Card>
                        </Box>
                      );
                    })()
                  )}
                  {plentyRuleError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {plentyRuleError}
                    </Alert>
                  )}
                </Box>
              )}
              {activeTab === 1 && (
                <Box>
                  <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                      Set the thresholds for categorizing{" "}
                      <strong>Amazon</strong> articles:
                      <br />
                      <strong>Penner:</strong> ratio ≤ penner threshold
                      <br />
                      <strong>Renner:</strong> ratio ≥ renner threshold
                      <br />
                      <strong>Neutral:</strong> between thresholds
                      <br />
                      Ratio = sold_articles / netto_stock
                    </Typography>
                  </Alert>
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                      <strong>Important:</strong> After changing threshold
                      values, you must click the "Recategorize Articles" button
                      in the Utilities section above to apply the new thresholds
                      to existing articles in the database.
                    </Typography>
                  </Alert>
                  {loadingAmazonRules ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", p: 2 }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : (
                    (() => {
                      const pennerRule = amazonRules.find(
                        (r) => r.rule_name === "penner",
                      );
                      const rennerRule = amazonRules.find(
                        (r) => r.rule_name === "renner",
                      );
                      return (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 4,
                            width: "100%",
                          }}
                        >
                          {/* Penner */}
                          <Card
                            sx={{
                              border: "3px solid #ff9800",
                              borderRadius: 3,
                              background:
                                "linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)",
                              boxShadow: "0 4px 12px rgba(255, 152, 0, 0.15)",
                            }}
                          >
                            <CardContent sx={{ p: 4 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 2,
                                  mb: 3,
                                }}
                              >
                                <TrendingDownIcon
                                  sx={{
                                    fontSize: 40,
                                    color: "#e65100",
                                  }}
                                />
                                <Box>
                                  <Typography
                                    variant="h5"
                                    sx={{
                                      color: "#e65100",
                                      fontWeight: "bold",
                                      mb: 1,
                                    }}
                                  >
                                    Penner Articles
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: "#bf360c" }}
                                  >
                                    Low-selling items • Ratio ≥{" "}
                                    {pennerRule
                                      ? pennerRule.threshold_value
                                      : "-"}
                                  </Typography>
                                </Box>
                              </Box>

                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 3,
                                  flexWrap: "wrap",
                                }}
                              >
                                <Box sx={{ flex: 1, minWidth: 200 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "#e65100",
                                      fontWeight: 500,
                                      mb: 1,
                                    }}
                                  >
                                    Threshold Value
                                  </Typography>
                                  <TextField
                                    type="number"
                                    size="small"
                                    value={
                                      amazonEditValues.penner ??
                                      (pennerRule
                                        ? pennerRule.threshold_value
                                        : "")
                                    }
                                    onChange={(e) =>
                                      handleEditChange(
                                        "penner",
                                        e.target.value,
                                        "amazon",
                                      )
                                    }
                                    inputProps={{
                                      step: "0.01",
                                      style: { fontSize: 18, fontWeight: 500 },
                                    }}
                                    sx={{
                                      "& .MuiInputBase-input": {
                                        fontSize: 18,
                                        fontWeight: 500,
                                      },
                                    }}
                                    fullWidth
                                  />
                                </Box>
                                <Button
                                  variant="contained"
                                  color="warning"
                                  size="large"
                                  startIcon={
                                    savingAmazon.penner ? (
                                      <CircularProgress size={20} />
                                    ) : (
                                      <SaveIcon />
                                    )
                                  }
                                  onClick={() =>
                                    handleSaveRule("penner", "amazon")
                                  }
                                  disabled={
                                    savingAmazon.penner ||
                                    amazonEditValues.penner ===
                                      (pennerRule
                                        ? pennerRule.threshold_value
                                        : undefined)
                                  }
                                  sx={{
                                    minWidth: 120,
                                    height: 48,
                                    fontSize: 16,
                                    fontWeight: "bold",
                                  }}
                                >
                                  {savingAmazon.penner ? "Saving..." : "Save"}
                                </Button>
                              </Box>
                            </CardContent>
                          </Card>

                          {/* Neutral */}
                          <Card
                            sx={{
                              border: "3px solid #43a047",
                              borderRadius: 3,
                              background:
                                "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)",
                              boxShadow: "0 4px 12px rgba(67, 160, 71, 0.15)",
                            }}
                          >
                            <CardContent sx={{ p: 4 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 2,
                                  mb: 2,
                                }}
                              >
                                <BalanceIcon
                                  sx={{
                                    fontSize: 40,
                                    color: "#2e7d32",
                                  }}
                                />
                                <Box>
                                  <Typography
                                    variant="h5"
                                    sx={{
                                      color: "#2e7d32",
                                      fontWeight: "bold",
                                      mb: 1,
                                    }}
                                  >
                                    Neutral Articles
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: "#1b5e20" }}
                                  >
                                    Balanced performance • Between thresholds
                                  </Typography>
                                </Box>
                              </Box>

                              <Box sx={{ mt: 2 }}>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    color: "#2e7d32",
                                    fontWeight: 500,
                                    textAlign: "center",
                                  }}
                                >
                                  {pennerRule && rennerRule
                                    ? `Ratio < ${pennerRule.threshold_value} and > ${rennerRule.threshold_value}`
                                    : "Articles with ratios between Penner and Renner thresholds"}
                                </Typography>
                              </Box>
                            </CardContent>
                          </Card>

                          {/* Renner */}
                          <Card
                            sx={{
                              border: "3px solid #1976d2",
                              borderRadius: 3,
                              background:
                                "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
                              boxShadow: "0 4px 12px rgba(25, 118, 210, 0.15)",
                            }}
                          >
                            <CardContent sx={{ p: 4 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 2,
                                  mb: 3,
                                }}
                              >
                                <TrendingUpIcon
                                  sx={{
                                    fontSize: 40,
                                    color: "#0d47a1",
                                  }}
                                />
                                <Box>
                                  <Typography
                                    variant="h5"
                                    sx={{
                                      color: "#0d47a1",
                                      fontWeight: "bold",
                                      mb: 1,
                                    }}
                                  >
                                    Renner Articles
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{ color: "#0d47a1" }}
                                  >
                                    High-selling items • Ratio ≤{" "}
                                    {rennerRule
                                      ? rennerRule.threshold_value
                                      : "-"}
                                  </Typography>
                                </Box>
                              </Box>

                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 3,
                                  flexWrap: "wrap",
                                }}
                              >
                                <Box sx={{ flex: 1, minWidth: 200 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "#0d47a1",
                                      fontWeight: 500,
                                      mb: 1,
                                    }}
                                  >
                                    Threshold Value
                                  </Typography>
                                  <TextField
                                    type="number"
                                    size="small"
                                    value={
                                      amazonEditValues.renner ??
                                      (rennerRule
                                        ? rennerRule.threshold_value
                                        : "")
                                    }
                                    onChange={(e) =>
                                      handleEditChange(
                                        "renner",
                                        e.target.value,
                                        "amazon",
                                      )
                                    }
                                    inputProps={{
                                      step: "0.01",
                                      style: { fontSize: 18, fontWeight: 500 },
                                    }}
                                    sx={{
                                      "& .MuiInputBase-input": {
                                        fontSize: 18,
                                        fontWeight: 500,
                                      },
                                    }}
                                    fullWidth
                                  />
                                </Box>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  size="large"
                                  startIcon={
                                    savingAmazon.renner ? (
                                      <CircularProgress size={20} />
                                    ) : (
                                      <SaveIcon />
                                    )
                                  }
                                  onClick={() =>
                                    handleSaveRule("renner", "amazon")
                                  }
                                  disabled={
                                    savingAmazon.renner ||
                                    amazonEditValues.renner ===
                                      (rennerRule
                                        ? rennerRule.threshold_value
                                        : undefined)
                                  }
                                  sx={{
                                    minWidth: 120,
                                    height: 48,
                                    fontSize: 16,
                                    fontWeight: "bold",
                                  }}
                                >
                                  {savingAmazon.renner ? "Saving..." : "Save"}
                                </Button>
                              </Box>
                            </CardContent>
                          </Card>
                        </Box>
                      );
                    })()
                  )}
                  {amazonRuleError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      {amazonRuleError}
                    </Alert>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Grid>
    </Grid>
  );
};

export default PennerRennerManagementSubTab;
